"use client";

import { EnvelopeIcon, PaperClipIcon } from "@heroicons/react/20/solid";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const getId = (item) => item?._id?.$oid || item?._id || item?.id;
const getTimestamp = (item) => item.receivedAt || item.sentAt;

const formatDateHeader = (date) =>
  new Date(date).toLocaleDateString("en-US", {
    weekday: "short",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const formatTime = (date) =>
  new Date(date).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

export default function CorrespondenceTimeline({
  correspondences,
  onSelect,
  selectedId,
}) {
  if (!correspondences || correspondences.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-gray-400">
        No correspondences found.
      </div>
    );
  }

  // Group by day
  const grouped = correspondences.reduce((acc, item) => {
    const day = new Date(getTimestamp(item)).toDateString();
    if (!acc[day]) acc[day] = [];
    acc[day].push(item);
    return acc;
  }, {});

  // Newest day first; newest message first within each day
  const sortedDates = Object.keys(grouped).sort(
    (a, b) => new Date(b) - new Date(a),
  );
  sortedDates.forEach((day) =>
    grouped[day].sort(
      (a, b) => new Date(getTimestamp(b)) - new Date(getTimestamp(a)),
    ),
  );

  return (
    <nav aria-label="Correspondence Timeline">
      {sortedDates.map((date) => (
        <div key={date} className="mb-8 last:mb-0">
          <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-400">
            {formatDateHeader(date)}
          </h3>

          <ol role="list">
            {grouped[date].map((item, idx) => {
              const itemId = getId(item);
              const isSelected = !!selectedId && selectedId === itemId;
              const isLast = idx === grouped[date].length - 1;
              const attachmentCount = item.attachments?.length || 0;

              return (
                <li
                  key={itemId || idx}
                  className={classNames("relative", !isLast && "pb-4")}
                >
                  {!isLast && (
                    <div
                      aria-hidden="true"
                      className={classNames(
                        "absolute left-7 top-10 -ml-px h-full w-0.5",
                        isSelected ? "bg-orange-500" : "bg-gray-600",
                      )}
                    />
                  )}

                  <button
                    onClick={() => onSelect(item)}
                    aria-current={isSelected ? "true" : undefined}
                    className={classNames(
                      "relative flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors duration-150",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500",
                      isSelected
                        ? "border-orange-500 bg-orange-500/15"
                        : "border-transparent bg-gray-600 hover:bg-gray-500",
                    )}
                  >
                    <span
                      className={classNames(
                        "relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full",
                        isSelected ? "bg-orange-500" : "bg-gray-800",
                      )}
                    >
                      <EnvelopeIcon
                        aria-hidden="true"
                        className={classNames(
                          "size-4",
                          isSelected ? "text-white" : "text-gray-200",
                        )}
                      />
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="flex items-start justify-between gap-2">
                        <span
                          className={classNames(
                            "truncate text-sm text-gray-100",
                            isSelected ? "font-semibold" : "font-medium",
                          )}
                        >
                          {item.subject || "(No subject)"}
                        </span>
                        <span className="shrink-0 text-xs text-gray-400">
                          {formatTime(getTimestamp(item))}
                        </span>
                      </span>

                      <span className="mt-0.5 flex items-center justify-between gap-2">
                        <span className="truncate text-xs text-gray-400">
                          {item.sender}
                        </span>
                        {attachmentCount > 0 && (
                          <span
                            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-gray-800 px-2 py-0.5 text-xs text-orange-300"
                            title={`${attachmentCount} attachment${
                              attachmentCount > 1 ? "s" : ""
                            }`}
                          >
                            <PaperClipIcon className="size-3" />
                            {attachmentCount}
                          </span>
                        )}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      ))}
    </nav>
  );
}
